/**
 * FetchDataClient - A modern API client using native fetch instead of axios
 * Drop-in replacement for DataClient with the same interface
 */

interface CustomRequestInit extends RequestInit {
  _retry?: boolean;
}

export default class FetchDataClient {
  private token: string | null;
  private baseURL: string;

  constructor(token: string | null = null) {
    this.token = null; // Always initialize as null and set explicitly
    this.baseURL = this.getBaseURL();

    // Set token if provided
    if (token) {
      this.setAxiosHeaders(token);
    }
  }

  private getBaseURL(): string {
    // In production, use the full API domain from environment variable
    // In development, use the relative URL for Vite's proxy feature
    const apiDomain = import.meta.env.VITE_API_DOMAIN;
    const apiPort = import.meta.env.VITE_API_PORT;
    const devMode = import.meta.env.VITE_DEV_MODE === 'true';

    if (devMode) {
      return '/api';
    }

    if (apiDomain) {
      // Ensure the domain has a protocol
      let fullDomain = apiDomain;
      if (
        !apiDomain.startsWith('http://') &&
        !apiDomain.startsWith('https://')
      ) {
        // Default to http:// for local development domains, https:// for others
        const isLocalDomain =
          apiDomain.includes('localhost') ||
          apiDomain.includes('127.0.0.1') ||
          apiDomain.endsWith('.test') ||
          apiDomain.endsWith('.local');
        fullDomain = `${isLocalDomain ? 'http' : 'https'}://${apiDomain}`;
      }

      // If we have a domain set, use it (production mode)
      return apiPort ? `${fullDomain}:${apiPort}/api` : `${fullDomain}/api`;
    }

    // Otherwise use relative URL for development proxy
    return '/api';
  }

  public setInitialToken(token: string | null): void {
    this.token = token;
  }

  private convertStringToNumber(value: unknown): unknown {
    if (typeof value !== 'string') return value;

    // Handle comma-separated number strings
    if (value.includes(',')) {
      // Check if it's a comma-separated list of numbers
      const parts = value.split(',');
      const allNumbers = parts.every(
        (part) => /^\d+$/.test(part.trim()) || /^\d*\.\d+$/.test(part.trim())
      );

      if (allNumbers) {
        return parts.map((part) => {
          const trimmed = part.trim();
          return /^\d*\.\d+$/.test(trimmed)
            ? parseFloat(trimmed)
            : parseInt(trimmed, 10);
        });
      }
      return value; // Not all parts are numbers, keep as string
    }

    // Integer check
    if (/^\d+$/.test(value)) return parseInt(value, 10);

    // Float check
    if (/^\d*\.\d+$/.test(value)) return parseFloat(value);

    return value;
  }

  private deepConvert(obj: unknown): unknown {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.deepConvert(item));
    } else if (typeof obj === 'object' && obj !== null) {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key,
          this.deepConvert(value),
        ])
      );
    } else {
      return this.convertStringToNumber(obj);
    }
  }

  /**
   * Common request handler with error handling and auto token refresh
   */
  private async request<T>(
    url: string,
    options: RequestInit,
    token: string | null = null
  ): Promise<T> {
    try {
      // Add trailing slash to baseURL if not present and url doesn't start with slash
      const endpoint = url.startsWith('/') ? url : `/${url}`;
      const fullUrl = `${this.baseURL}${endpoint}`;

      // Create the headers for this request
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };

      // Prioritize passed token, then fall back to instance token
      const authToken = token || this.token;

      // Add Authorization header if token exists
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      } else {
        console.warn(`No token available for request to ${url}`);
      }

      // Prepare the full request options
      const requestOptions: RequestInit = {
        ...options,
        headers,
        credentials: 'include',
      };
      // Perform the fetch
      const response = await fetch(fullUrl, requestOptions);

      // Check for response errors
      if (!response.ok) {
        if (response.status === 401) {
          console.error(`401 Unauthorized for request to ${url}`);
          throw new Error(`Authentication required for ${url}`);
        }

        // For other errors, try to get response text
        let errorMessage = `HTTP error ${response.status}`;
        try {
          const errorText = await response.text();
          errorMessage = `${errorMessage}: ${errorText}`;
        } catch (textError) {
          // If we can't get the text, just use the status
        }

        throw new Error(errorMessage);
      }

      // Parse response data
      const contentType = response.headers.get('content-type');
      let data: unknown;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Convert string numbers to number type if applicable
      return this.deepConvert(data) as T;
    } catch (error) {
      console.error('Fetch request failed:', error);
      throw error;
    }
  }

  /**
   * GET request
   */
  public async get<T>(url: string, token: string | null = null): Promise<T> {
    return this.request<T>(url, { method: 'GET' }, token);
  }

  /**
   * POST request
   */
  public async post<T, D = unknown>(
    url: string,
    data: D,
    token: string | null = null
  ): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  }

  /**
   * PUT request
   */
  public async put<T, D = unknown>(
    url: string,
    data: D,
    token: string | null = null
  ): Promise<T> {
    return this.request<T>(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      token
    );
  }

  /**
   * DELETE request
   */
  public async delete<T>(url: string, token: string | null = null): Promise<T> {
    return this.request<T>(url, { method: 'DELETE' }, token);
  }

  /**
   * Set the authentication token for all future requests
   * @param token The JWT token or null to clear authentication
   */
  public setAxiosHeaders(token: string | null): void {
    this.token = token;
  }

  /**
   * Get the current authentication token
   * @returns The current token or null if not authenticated
   */
  public getAuthToken(): string | null {
    return this.token;
  }

  /**
   * Request password reset email
   */
  public async requestPasswordReset(
    email: string
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>('/password/email', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /**
   * Reset password with token
   */
  public async resetPassword(
    token: string,
    email: string,
    password: string,
    passwordConfirmation: string
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>('/password/reset', {
      method: 'POST',
      body: JSON.stringify({
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      }),
    });
  }
}
